import { File, FilePondErrorDescription } from "filepond";
import React from "react";
import Services from "../../services/Services";
import FilePondComponent from "../forms/FilePondComponent";
import { connect } from "react-redux";
import { IApplicationState } from "../../store";
import { selectAuth } from "../../store/auth/selectors";
import IAuthenticatedComponentProps from "../../models/IAuthenticatedComponent";
import { AttachmentTypes, getAttachmentTypeString } from "../../functions/getAttachmentType";
import { DialogContentErrorText, DialogContentSuccessText } from "../../functions/errorHandling";

export interface IIntegrationAttachmentUploadProps extends IAuthenticatedComponentProps {
  integrationId: string;
}

const IntegrationAttachmentUpload = (props: IIntegrationAttachmentUploadProps) => {
  const { token, integrationId } = props;

  const [serverConfig, setServerConfig] = React.useState<any>();
  const [uploadSuccess, setUploadSuccess] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>();
  const readFile = async (file: Blob): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = function(e: any) {
        const result = e.target.result;
        resolve(result);
      };

      reader.onerror = function(e: any) {
        reject(e);
      };

      reader.readAsText(file);
    });
  };

  const validateFieldsXMLFile = async (file: File): Promise<boolean> => {
    try {
      setErrorMessage(null);

      if (!token) {
        throw Error("Unable to load credentials required for submitting attachments. Please refresh the page and submit a service call if this persists.");
      }

      let fieldsXML = await readFile(file.file);
      if (!fieldsXML) {
        throw Error("Failed to read text from file.");
      }

      let repo = await new Services().IntegrationRepository();
      await repo.validateIntegrationFields(token, fieldsXML);
    } catch (err) {
      setErrorMessage(`${err}`);
      return false;
    }

    return true;
  };

  const processfile = (error: FilePondErrorDescription | null, file: File) => {
    if (!error) {
      setUploadSuccess(true);
      return;
    }

    setUploadSuccess(false);
    setErrorMessage(`Status Code: ${error.code} ${error.body ? "Message:" : ""} ${error.body}`);
  };

  React.useEffect(() => {
    const init = async (): Promise<any> => {
      const repo = await new Services().AttachmentRepository();
      return repo.getServerConfig(token, getAttachmentTypeString(AttachmentTypes.Integration), integrationId, "QmlativSDKFile");
    };
    init().then(serverConfig => {
      setServerConfig(serverConfig);
    });
  }, []);

  return (
    <div>
      {serverConfig && (
        <FilePondComponent
          allowMultiple={false}
          instantUpload={true}
          beforeAddFile={validateFieldsXMLFile}
          chunkUploads={true}
          chunkSize={4096}
          chunkForce={true}
          onprocessfile={processfile}
          server={serverConfig}
          allowRevert={false}
        />
      )}
      {uploadSuccess && <DialogContentSuccessText>The file was successfully uploaded. You may now view your pending field changes.</DialogContentSuccessText>}
      {errorMessage && (
        <>
          <DialogContentErrorText>
            There was an error while uploading your file. If you need assistance determining how to resolve this, please submit a service call.
          </DialogContentErrorText>
          <DialogContentErrorText>{errorMessage}</DialogContentErrorText>
        </>
      )}
    </div>
  );
};

export default connect((state: IApplicationState) => ({
  ...selectAuth(state),
}))(IntegrationAttachmentUpload);
